(() => {
  // Reuse the native form constructor/error UI, but not its optional extra-item flow.
  customElements.whenDefined('product-form').then(() => {
    if (customElements.get('landing-product-form')) return;

    const Engine = window.LandingPurchaseEngine;

    class LandingProductForm extends customElements.get('product-form') {
      connectedCallback() {
        if (this.initialized) {
          if (this.mediaList) this.resizeObserver?.observe(this.mediaList);
          this.startTimer();
          return;
        }
        this.initialized = true;
        this.variantInput = this.form.querySelector('[name="id"]');
        this.variantSelect = this.querySelector('[data-landing-variant]');
        this.price = this.querySelector('[data-landing-price]');
        this.availability = this.querySelector('[data-landing-availability]');
        this.cta = this.querySelector('[data-landing-cta]');
        this.status = this.querySelector('[data-landing-status]');
        this.cartLink = this.querySelector('[data-landing-cart-link]');
        this.spinner = this.querySelector('.loading__spinner');
        this.variantTitle = this.querySelector('[data-landing-variant-title]');
        this.swatches = this.querySelector('[data-landing-swatches]');
        this.swatches.addEventListener('click', (event) => {
          const button = event.target.closest('[data-landing-variant-button]');
          if (!button || button.disabled) return;
          this.variantSelect.value = button.dataset.landingVariantButton;
          if (!this.requiresCartCheck) this.handleErrorMessage();
          this.updateVariant();
        });
        this.variantSelect.addEventListener('change', () => {
          if (!this.requiresCartCheck) this.handleErrorMessage();
          this.updateVariant();
        });
        this.querySelectorAll('[data-landing-media-target]').forEach((button) => {
          button.addEventListener('click', () => this.showMedia(button.dataset.landingMediaTarget));
        });
        this.mediaList = this.querySelector('[data-landing-media-list]');
        if (this.mediaList) {
          this.mediaPanels = Array.from(this.querySelectorAll('[data-landing-media]'));
          this.mediaList.addEventListener('scroll', () => {
            cancelAnimationFrame(this.mediaFrame);
            this.mediaFrame = requestAnimationFrame(() => {
              const nearest = this.mediaPanels.reduce((a, b) =>
                Math.abs(a.offsetLeft - this.mediaList.scrollLeft) < Math.abs(b.offsetLeft - this.mediaList.scrollLeft) ? a : b
              );
              this.updateMediaState(nearest.dataset.landingMedia);
            });
          }, { passive: true });
          this.mediaList.addEventListener('keydown', (event) => {
            if (event.target !== this.mediaList || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
            event.preventDefault();
            this.stepMedia(event.key === 'ArrowLeft' ? -1 : 1);
          });
          this.querySelectorAll('[data-landing-media-step]').forEach((button) => {
            button.addEventListener('click', () => this.stepMedia(Number(button.dataset.landingMediaStep)));
          });
          this.resizeObserver = new ResizeObserver(() => {
            if (this.activeMediaId) this.showMedia(this.activeMediaId, 'instant');
          });
          this.resizeObserver.observe(this.mediaList);
          const initial = this.mediaPanels.find((panel) => panel.classList.contains('is-active')) || this.mediaPanels[0];
          this.showMedia(initial.dataset.landingMedia, 'instant');
        }
        this.startTimer();
        this.initializeVariants();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        clearInterval(this.timerInterval);
        cancelAnimationFrame(this.mediaFrame);
      }

      async initializeVariants() {
        try {
          // Liquid paginates variants at 250. Load remaining real variants before enabling purchases.
          await this.loadVariantPages();
          this.variantSelect.value = this.variantInput.value;
          if (!this.variantSelect.selectedOptions[0]) throw new Error('Selected variant is missing.');
          this.initializeOffers();
          this.ready = true;
          this.updateVariant();
        } catch (error) {
          this.ready = false;
          this.updateDisabledState();
          this.handleErrorMessage('We could not load this product. Please refresh the page.');
          console.error('[landing-product-purchase] Variant initialization failed:', error);
        }
      }

      async loadVariantPages() {
        const pages = this.querySelector('[data-landing-variant-pages]');
        const prices = this.querySelector('[data-landing-price-templates]');
        const ids = new Set(Array.from(this.variantSelect.options, (option) => option.value));
        const visited = new Set();
        let next = pages.dataset.nextPage;
        if (pages.dataset.currentPage !== '1') {
          const firstPage = new URL(window.location.href);
          firstPage.searchParams.set(pages.dataset.pageParam, '1');
          next = firstPage.href;
        }
        while (next) {
          const url = new URL(next, window.location.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          if (url.origin !== window.location.origin || visited.has(url.href)) {
            throw new Error('Invalid variant pagination.');
          }
          visited.add(url.href);
          const response = await fetch(url.href);
          if (!response.ok) throw new Error('Unable to load variants.');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = html.getElementById(this.id);
          const sourcePages = source?.querySelector('[data-landing-variant-pages]');
          if (!sourcePages) throw new Error('Missing variant section.');
          source.querySelectorAll('[data-landing-variant] option').forEach((option) => {
            if (ids.has(option.value)) return;
            const price = source.querySelector(`[data-landing-variant-price="${option.value}"]`);
            const swatch = source.querySelector(`[data-landing-variant-button="${option.value}"]`);
            if (!price || !swatch) throw new Error('Missing variant presentation.');
            this.variantSelect.append(option);
            prices.append(price);
            this.swatches.append(swatch);
            ids.add(option.value);
          });
          next = sourcePages.dataset.nextPage;
        }
        if (ids.size !== Number(pages.dataset.variantCount)) throw new Error('Incomplete variant list.');
      }


      initializeOffers() {
        this.useProductPricing = this.dataset.useProductPricing === 'true';
        this.variants = Array.from(this.variantSelect.options, (option) => ({
          id: option.value, title: option.dataset.title, image: option.dataset.image,
          available: option.dataset.available === 'true', purchasable: option.dataset.purchasable === 'true',
          price: Engine.money(option.dataset.price), compare: Engine.money(option.dataset.comparePrice),
          saving: option.dataset.variantSaving,
        }));
        const initial = this.variants.find((variant) => variant.id === this.variantSelect.value);
        if (!Engine.purchasable(initial)) this.variantSelect.value = this.variants.find(Engine.purchasable)?.id || '';
        this.selectedMainVariant = this.variantSelect.value;
        this.singleGift = this.querySelector('[data-landing-single-gift]');
        this.singleGiftInput = this.singleGift?.querySelector('[data-addon-input]');
        this.singleGiftInput?.addEventListener('change', () => {
          this.singleGiftInput.closest('[data-addon-card]').classList.toggle('product-addon-card--selected', this.singleGiftInput.checked);
          this.updatePurchase();
        });
        this.offers = Array.from(this.querySelectorAll('[data-landing-offer]'), (element) => {
          const offer = { ...Engine.offer(element.dataset), element };
          offer.watchSelections = Engine.initialSelections(offer, this.variants);
          const container = element.querySelector('[data-landing-watch-selectors]');
          offer.watchSelections.forEach((id, index) => {
            const fragment = this.querySelector('[data-landing-watch-template]').content.cloneNode(true);
            const row = fragment.querySelector('[data-landing-watch]');
            const select = row.querySelector('select');
            select.id = `${this.id}-watch-${offer.slot}-${index + 1}`;
            select.dataset.position = String(index + 1);
            select.dataset.role = index < offer.paid ? 'paid' : 'free';
            row.querySelector('label').htmlFor = select.id;
            row.querySelector('.landing-variant-selector__position-number').textContent = String(index + 1);
            row.querySelector('[data-landing-watch-label]').textContent = `Watch ${index + 1}, ${select.dataset.role}`;
            row.querySelector('[data-landing-watch-role]').textContent = index < offer.paid ? '' : (offer.discount || this.variants.find((v) => v.id === id)?.price === 0 ? 'Free' : 'Bonus');
            for (const option of this.variantSelect.options) {
              const copy = option.cloneNode(true);
              copy.selected = false;
              copy.disabled = copy.dataset.available !== 'true' || copy.dataset.purchasable !== 'true';
              select.append(copy);
            }
            select.value = id;
            select.addEventListener('change', () => {
              if (this.pending) return;
              offer.watchSelections[index] = select.value;
              this.updateWatchImage(select);
              this.updatePurchase();
            });
            this.updateWatchImage(select);
            container.append(fragment);
          });
          element.querySelector('[data-landing-offer-radio]')?.addEventListener('change', () => {
            if (this.pending) return;
            this.activeOffer = offer;
            if (!this.requiresCartCheck) this.handleErrorMessage();
            this.updatePurchase();
          });
          return offer;
        });
        // Source default: the last configured slot; a lone slot is automatically active.
        this.activeOffer = this.offers.at(-1) || null;
      }

      updateWatchImage(select) {
        const option = select.selectedOptions[0];
        const image = select.closest('[data-landing-watch]').querySelector('img');
        image.hidden = !option?.dataset.image;
        if (option?.dataset.image) image.src = option.dataset.image;
        image.alt = option?.dataset.title || '';
      }

      readGift(element) {
        return {
          id: element.dataset.giftId, label: element.dataset.giftTitle, url: element.dataset.giftUrl,
          available: element.dataset.giftAvailable === 'true', purchasable: element.dataset.giftPurchasable === 'true',
          price: Engine.money(element.dataset.giftPrice), kind: 'free_gift', quantityPolicy: 'fixed_one', quantity: 1,
        };
      }

      selectedWatches(offer = this.activeOffer) {
        return Engine.watches(offer, this.selectedMainVariant, offer?.watchSelections || [], this.variants);
      }

      updateVariant() {
        const option = this.variantSelect.selectedOptions[0];
        this.selectedMainVariant = option?.value || '';
        this.variantInput.value = this.selectedMainVariant;
        this.variantTitle.textContent = option?.dataset.title || '';
        this.swatches.querySelectorAll('[data-landing-variant-button]').forEach((button) => {
          const active = button.dataset.landingVariantButton === this.selectedMainVariant;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        if (option?.dataset.mediaId) this.showMedia(option.dataset.mediaId);
        this.updatePurchase();
      }

      renderPrice(target, offer) {
        if (!target) return;
        const pricing = Engine.pricing(offer, this.useProductPricing, this.selectedWatches(offer));
        const template = pricing.configured
          ? offer.element.querySelector('[data-landing-offer-price-template]')
          : this.querySelector(`[data-landing-variant-price="${pricing.variantId}"]`);
        target.replaceChildren(...(template ? [template.content.cloneNode(true)] : []));
      }

      updatePurchase() {
        if (!this.offers) return;
        this.watchSelections = this.activeOffer?.watchSelections || [];
        const watches = this.selectedWatches();
        const singleGiftActive = this.activeOffer?.includesGift && this.activeOffer.total === 1;
        this.singleGift.hidden = !singleGiftActive;
        this.configuredGifts = !this.activeOffer?.includesGift ? [] : singleGiftActive
          ? [this.readGift(this.singleGift)]
          : Array.from(this.activeOffer.element.querySelectorAll('[data-landing-gift]'), (element) => this.readGift(element));
        this.giftSelections = singleGiftActive && !this.singleGiftInput?.checked ? [] : this.configuredGifts;
        this.configurationError = this.activeOffer?.runtimeError || Engine.validate(this.activeOffer, watches, this.giftSelections, this.configuredGifts);
        const error = this.querySelector('[data-landing-configuration-error]');
        error.textContent = this.configurationError;
        error.hidden = !this.configurationError;
        this.available = watches.length === (this.activeOffer?.total || 1) && watches.every(Engine.purchasable);
        this.availability.textContent = this.available ? this.dataset.availableLabel : this.dataset.soldOutLabel;
        this.availability.classList.toggle('is-unavailable', !this.available);
        this.cta.textContent = this.available ? this.dataset.ctaLabel : this.dataset.soldOutLabel;
        this.renderPrice(this.price, this.activeOffer);
        for (const offer of this.offers) {
          const active = offer === this.activeOffer;
          offer.element.classList.toggle('is-active', active);
          const radio = offer.element.querySelector('[data-landing-offer-radio]');
          if (radio) {
            radio.checked = active;
            radio.setAttribute('aria-expanded', String(active && offer.total > 1));
          }
          offer.element.querySelector('[data-landing-offer-details]').hidden = !active || offer.total <= 1;
          this.renderPrice(offer.element.querySelector('[data-landing-offer-price]'), offer);
          const saving = offer.element.querySelector('[data-landing-saving]');
          if (saving) {
            const pricing = Engine.pricing(offer, this.useProductPricing, this.selectedWatches(offer));
            const amount = pricing.configured ? offer.element.dataset.saving : this.selectedWatches(offer)[0]?.saving;
            saving.hidden = !amount;
            saving.textContent = amount ? `Save ${amount}` : '';
          }
        }
        const note = this.querySelector('[data-landing-pricing-note]');
        const variantPrice = !this.activeOffer || this.useProductPricing || this.activeOffer.price === null;
        if (note) {
          note.hidden = !this.activeOffer;
          note.textContent = variantPrice && this.activeOffer?.total > 1
            ? 'Price shown is for watch 1. Shopify calculates the full offer in your cart.'
            : this.activeOffer?.free > 0 ? 'The configured discount is applied and free watches are verified in your cart.'
            : 'Offer pricing is verified against Shopify when added to your cart.';
        }
        this.updateDisabledState();
      }

      updateDisabledState() {
        const disabled = !this.ready || !this.available || this.configurationError || this.pending || this.requiresCartCheck;
        this.submitButton.disabled = !!disabled;
        this.submitButton.setAttribute('aria-disabled', String(!!disabled));
        this.variantSelect.disabled = !this.ready || !!this.pending;
        this.swatches.querySelectorAll('[data-landing-variant-button]').forEach((button) => {
          const variant = this.variants?.find((item) => item.id === button.dataset.landingVariantButton);
          button.disabled = this.variantSelect.disabled || !Engine.purchasable(variant);
        });
        for (const offer of this.offers || []) {
          const radio = offer.element.querySelector('[data-landing-offer-radio]');
          if (radio) radio.disabled = !this.ready || !!this.pending;
          offer.element.querySelectorAll('select').forEach((select) => {
            select.disabled = !this.ready || !!this.pending || offer !== this.activeOffer;
          });
        }
        if (this.singleGiftInput) this.singleGiftInput.disabled = !this.ready || !!this.pending || this.singleGift.hidden;
      }

      startTimer() {
        const timer = this.querySelector('[data-landing-sale-countdown]');
        if (!timer) return;
        clearInterval(this.timerInterval);
        const key = `landing-sale-deadline:${window.location.pathname}:global`;
        let stored = this.saleDeadline || 0;
        try { stored = Number(localStorage.getItem(key)) || stored; } catch (_) { /* Private browsing: use memory. */ }
        this.saleDeadline = Engine.nextDeadline(stored, Date.now());
        const save = () => { try { localStorage.setItem(key, String(this.saleDeadline)); } catch (_) { /* Memory remains valid. */ } };
        save();
        const render = () => {
          const remaining = Math.max(0, Math.ceil((this.saleDeadline - Date.now()) / 1000));
          timer.textContent = [Math.floor(remaining / 3600), Math.floor((remaining % 3600) / 60), remaining % 60].map((n) => String(n).padStart(2, '0')).join(':');
          timer.dateTime = new Date(this.saleDeadline).toISOString();
          // Preserve the source's visible zero tick, then begin the next recurring day.
          if (remaining === 0) { this.saleDeadline = Engine.nextDeadline(0, Date.now()); save(); }
        };
        render();
        this.timerInterval = setInterval(render, 1000);
      }

      stepMedia(step) {
        const index = this.mediaPanels.findIndex((panel) => panel.dataset.landingMedia === this.activeMediaId);
        const next = this.mediaPanels[Math.max(0, Math.min(this.mediaPanels.length - 1, index + step))];
        this.showMedia(next.dataset.landingMedia);
      }

      showMedia(id, behavior = 'smooth') {
        const target = this.querySelector(`[data-landing-media="${id}"]`);
        if (!target || !this.mediaList) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) behavior = 'instant';
        this.updateMediaState(id);
        this.mediaList.scrollTo({ left: target.offsetLeft, behavior });
      }

      updateMediaState(id) {
        const changed = this.activeMediaId !== id;
        if (changed) window.pauseAllMedia();
        this.activeMediaId = id;
        this.querySelectorAll('[data-landing-media]').forEach((panel) => {
          const active = panel.dataset.landingMedia === id;
          if (!active) panel.querySelectorAll('video').forEach((video) => video.pause());
          panel.classList.toggle('is-active', active);
          panel.toggleAttribute('inert', !active);
        });
        this.querySelectorAll('[data-landing-media-target]').forEach((button) => {
          const active = button.dataset.landingMediaTarget === id;
          button.setAttribute('aria-pressed', String(active));
          button.setAttribute('aria-current', String(active));
          if (active && changed) {
            const item = button.parentElement;
            const list = item.parentElement;
            // Scroll the thumbnail rail only, without moving the page or its sticky header.
            list.scrollTo({
              left: Math.max(0, item.offsetLeft - (list.clientWidth - item.offsetWidth) / 2),
              top: Math.max(0, item.offsetTop - (list.clientHeight - item.offsetHeight) / 2),
              behavior: 'instant',
            });
          }
        });
        const index = this.mediaPanels.findIndex((panel) => panel.dataset.landingMedia === id);
        this.querySelectorAll('[data-landing-media-step]').forEach((button) => {
          button.disabled = Number(button.dataset.landingMediaStep) < 0 ? index <= 0 : index >= this.mediaPanels.length - 1;
        });
      }

      publishCartEvent(eventName, data) {
        // Breitvacher's publish is synchronous; a subscriber failure must not retry an add.
        try {
          publish(eventName, data);
        } catch (error) {
          console.error('[landing-product-purchase] Cart subscriber failed:', error);
        }
      }


      async readCart() {
        const response = await fetch(`${window.routes.cart_url}.js`, { cache: 'no-store', signal: AbortSignal.timeout(20000) });
        if (!response.ok) throw new Error('Unable to verify the Shopify cart.');
        const cart = await response.json();
        if (!Array.isArray(cart.items)) throw new Error('Invalid Shopify cart response.');
        return cart;
      }

      async cartRequest(url, body) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify(body);
        config.signal = AbortSignal.timeout(20000);
        const response = await fetch(url, config);
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(typeof result.description === 'string' ? result.description : 'Shopify could not complete this offer. Check your cart.');
        }
        return result;
      }

      async validateFreshGifts(gifts) {
        for (const gift of gifts) {
          const url = new URL(`${gift.url}.js`, window.location.origin);
          if (url.origin !== window.location.origin) throw new Error('Invalid gift product URL.');
          const response = await fetch(url.href, { cache: 'no-store', signal: AbortSignal.timeout(20000) });
          if (!response.ok) throw new Error('An included gift could not be checked. Please refresh.');
          const product = await response.json();
          const variant = product.variants?.find((item) => String(item.id) === gift.id);
          if (!variant?.available || variant.price !== 0 || variant.requires_selling_plan) {
            throw new Error('An included gift is unavailable or is no longer free. Please contact us.');
          }
        }
      }

      async refreshCart(cartState, parent) {
        const sections = this.cart?.getSectionsToRender().map((section) => section.id) || [];
        if (!this.cart) { window.location.assign(window.routes.cart_url); return; }
        const url = new URL(window.routes.cart_url, window.location.origin);
        url.searchParams.set('sections', sections.join(','));
        const response = await fetch(url.href, { cache: 'no-store', signal: AbortSignal.timeout(20000) });
        if (!response.ok) throw new Error('Unable to refresh cart sections.');
        const rendered = await response.json();
        if (sections.some((id) => typeof rendered[id] !== 'string')) throw new Error('Missing cart sections.');
        const result = { ...cartState, ...parent, items: cartState.items, sections: rendered };
        this.cart.classList.toggle('is-empty', cartState.item_count === 0);
        this.cart.renderContents(result);
      }

      async rejectOffer(cartState, group, message) {
        // Remove only this attempt's uniquely tagged lines, never a previous purchase.
        const updates = Object.fromEntries(cartState.items.filter((line) => line.properties?._landing_group === group).map((line) => [line.key, 0]));
        if (Object.keys(updates).length) await this.cartRequest(window.routes.cart_update_url, { updates });
        const remaining = await this.readCart();
        if (remaining.items.some((line) => line.properties?._landing_group === group)) throw new Error('The incomplete offer could not be removed. Please check your cart.');
        this.handleErrorMessage(`${message} This offer was removed from your cart.`);
        if (this.activeOffer) this.activeOffer.runtimeError = message;
        this.publishCartEvent(PUB_SUB_EVENTS.cartUpdate, { source: 'landing-product-form', cartData: remaining });
        try { await this.refreshCart(remaining); } catch (_) { if (this.cartLink) this.cartLink.hidden = false; }
      }

      async onSubmitHandler(event) {
        event.preventDefault();
        if (!this.ready || this.pending || this.requiresCartCheck || LandingProductForm.purchasePending) return;
        this.updatePurchase();
        if (!this.available || this.configurationError) return;
        const offer = this.activeOffer;
        const watches = this.selectedWatches();
        const gifts = [...this.giftSelections];
        const group = this.createAddonGroupId();
        const items = Engine.watchItems(offer, watches, group, this.useProductPricing);
        this.pending = true;
        LandingProductForm.purchasePending = true;
        this.updateDisabledState();
        this.handleErrorMessage();
        this.status.textContent = '';
        if (this.cartLink) this.cartLink.hidden = true;
        this.submitButton.classList.add('loading');
        this.spinner.classList.remove('hidden');
        this.form.setAttribute('aria-busy', 'true');
        let requestStarted = false;
        let verified = false;
        try {
          this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
          this.cart?.setActiveElement(this.submitButton);
          // Fail before mutations if gifts have changed since the Liquid page was rendered.
          await this.validateFreshGifts(this.configuredGifts);
          const before = await this.readCart();
          let children = [];
          if (gifts.length) {
            const form = new FormData();
            form.set('quantity', '1');
            Object.entries(items[0].properties).forEach(([key, value]) => form.set(`properties[${key}]`, value));
            // Reuse Breitvacher's add-on property and quantity contract.
            const addonItems = this.createAddonItems(form, gifts, { dataset: { addonSetId: offer.key, addonSetName: offer.key } }, items[0].id, group);
            items[0] = addonItems[0];
            children = addonItems.slice(1).map((child, index) => {
              delete child.parent_id;
              child.properties = { ...child.properties, _landing_group: group, _landing_offer: offer.key,
                _landing_option: offer.slot, _landing_gift: 'true', _landing_gift_position: String(index + 1) };
              return child;
            });
          }
          requestStarted = true;
          await this.cartRequest(window.routes.cart_add_url, { items });
          let state = await this.readCart();
          const parent = state.items.find((line) => line.properties?._landing_group === group && line.properties._landing_position === '1');
          if (!parent?.key) throw new Error('The selected watches could not be confirmed in the cart.');
          if (children.length) {
            // Exact line key avoids ambiguous parent_id when two watches use the same variant.
            children.forEach((child) => { child.parent_line_key = parent.key; });
            await this.cartRequest(window.routes.cart_add_url, { items: children });
            state = await this.readCart();
          }
          const discount = Engine.mergedDiscount(state, offer?.discount);
          if (discount) {
            await this.cartRequest(window.routes.cart_update_url, { discount });
            state = await this.readCart();
          }
          const appliedCodes = Engine.discountCodes(state).map((code) => code.toLowerCase());
          const codeRejected = discount && (!appliedCodes.includes(offer.discount.toLowerCase()) ||
            state.discount_codes?.some((entry) => entry.code.toLowerCase() === offer.discount.toLowerCase() && entry.applicable === false));
          const lostCode = (before.discount_codes || []).some((entry) => entry.applicable === true &&
            (!appliedCodes.includes(entry.code.toLowerCase()) || state.discount_codes?.some((after) => after.code.toLowerCase() === entry.code.toLowerCase() && after.applicable === false)));
          const problem = codeRejected ? 'The configured Shopify discount is not applicable to this offer.'
            : lostCode ? 'This offer discount cannot combine with your existing discount.'
            : Engine.verifyCart(state, group, watches, gifts);
          if (problem) { await this.rejectOffer(state, group, problem); return; }
          verified = true;
          if (this.status) this.status.textContent = '';
          if (this.cartLink) this.cartLink.hidden = false;
          this.publishCartEvent(PUB_SUB_EVENTS.cartUpdate, { source: 'landing-product-form', productVariantId: watches[0].id, cartData: state });
          const freshParent = state.items.find((line) => line.properties?._landing_group === group && line.properties._landing_position === '1');
          await this.refreshCart(state, freshParent);
        } catch (error) {
          console.error('[landing-product-purchase] Purchase flow failed:', error);
          if (requestStarted) {
            this.requiresCartCheck = true;
            if (this.cartLink) this.cartLink.hidden = false;
            if (verified) {
              // UI failure after confirmed commerce: recovery never resubmits an add.
              window.location.assign(window.routes.cart_url);
            } else {
              this.handleErrorMessage(`${error.message} The cart may already contain items. Check it before trying again.`);
              // Refresh is read-only; an ambiguous mutation is never automatically retried.
              try { await this.refreshCart(await this.readCart()); } catch (_) { /* Keep the cart link available. */ }
            }
          } else {
            this.handleErrorMessage(error.message || window.cartStrings.error);
          }
          this.publishCartEvent(PUB_SUB_EVENTS.cartError, { source: 'landing-product-form', message: error.message });
        } finally {
          this.pending = false;
          LandingProductForm.purchasePending = false;
          this.submitButton.classList.remove('loading');
          this.spinner.classList.add('hidden');
          this.form.removeAttribute('aria-busy');
          this.updatePurchase();
        }
      }

    }

    customElements.define('landing-product-form', LandingProductForm);
  });
})();
